<template>
  <div>
    <vs-row class="mb-5">
      <vs-col vs-w="8">
        <div class="content-area__heading">
          <h2 class="mb-1">Member Details</h2>
        </div>
      </vs-col>
      <vs-col vs-w="4" v-if="member.$hasLink('member-report')">
        <vs-row>
          <vs-col vs-w="6">
            <flat-pickr v-if="datepickerConfig"
                        class="w-full"
                        :value="selectedPeriod"
                        :config="datepickerConfig"
                        @on-close="onChangeSelectedPeriod"/>
          </vs-col>
          <vs-col vs-w="6" class="px-3" v-if="this.member.stats">
            <member-stats-report v-if="this.member.$hasLink('report')" :key="refreshKey" :member="member" :selected-period="selectedPeriod"/>
          </vs-col>
        </vs-row>
      </vs-col>
    </vs-row>

    <vs-row>
      <vs-col vs-w="4.5">
        <vx-card class="h-full" :title="$t('view_member.block.information.title')">
          <vs-row class="py-2">
            <vs-col vs-w="5">
              <span>{{ $t('view_member.block.information.fields.firstname') }}</span>
            </vs-col>
            <vs-col vs-w="7">
              <strong>{{ member.firstName }}</strong>
            </vs-col>
          </vs-row>
          <vs-row class="py-2">
            <vs-col vs-w="5">
              <span>{{ $t('view_member.block.information.fields.lastname') }}</span>
            </vs-col>
            <vs-col vs-w="7">
              <strong>{{ member.lastName ? member.lastName : '--' }}</strong>
            </vs-col>
          </vs-row>
          <vs-row class="py-2">
            <vs-col vs-w="5">
              <span>{{ $t('view_member.block.information.fields.email') }}</span>
            </vs-col>
            <vs-col vs-w="7">
              <strong style="word-break: break-word;">{{ member.email }}</strong>
            </vs-col>
          </vs-row>
          <vs-row class="py-2">
            <vs-col vs-w="5">
              <span>{{ userIdLabel }}</span>
            </vs-col>
            <vs-col vs-w="7">
              <strong>{{ member.claimNumber }}</strong>
            </vs-col>
          </vs-row>
          <vs-row v-if="showPolicyNumberLabel" class="py-2">
            <vs-col vs-w="5">
              <span>{{ policyNumberLabel }}</span>
            </vs-col>
            <vs-col vs-w="7">
              <strong>{{ member.policyNumber }}</strong>
            </vs-col>
          </vs-row>
          <vs-row class="py-2">
            <vs-col vs-w="5">
              <span>{{ $t('view_member.block.information.fields.member_chosen_name') }}</span>
            </vs-col>
            <vs-col vs-w="7">
              <strong>{{ member.customerChosenName }}</strong>
            </vs-col>
          </vs-row>
          <vs-row class="py-2">
            <vs-col vs-w="5">
              <span>{{ $t('view_member.block.details.fields.language') }}</span>
            </vs-col>
            <vs-col vs-w="7">
              <strong v-if="member.$hasEmbedded('locale')">
                {{ member.$getEmbedded('locale').name }}
              </strong>
            </vs-col>
          </vs-row>
        </vx-card>
      </vs-col>

      <vs-col vs-w="4.5" class="px-6">
        <vx-card class="h-full" :title="$t('view_member.block.details.title')">
          <vs-row class="py-2">
            <vs-col vs-w="5">
              <span>{{ $t('view_member.block.details.fields.organization') }}</span>
            </vs-col>
            <vs-col vs-w="7">
              <strong v-if="member.$hasEmbedded('organization')">
                {{ member.$getEmbedded('organization').name }}</strong>
            </vs-col>
          </vs-row>
          <vs-row class="py-2">
            <vs-col vs-w="5">
              <span>{{ $t('view_member.block.details.fields.program') }}</span>
            </vs-col>
            <vs-col vs-w="7">
              <strong v-if="member.$hasEmbedded('access-code')">
                {{ $t('filters.REFERRAL_PROGRAMS.values.' + member.$getEmbedded('access-code').program) }}
              </strong>
            </vs-col>
          </vs-row>
          <vs-row class="py-2">
            <vs-col vs-w="5">
                <span>{{ businessUnitLabel ? businessUnitLabel : $t('view_member.block.details.fields.business_unit') }}</span>
            </vs-col>
            <vs-col vs-w="7">
              <strong v-if="member.$hasEmbedded('business-unit')">
                {{ member.$getEmbedded('business-unit').name }}
              </strong>
            </vs-col>
          </vs-row>
          <vs-row class="py-2">
            <vs-col vs-w="5">
              <span>{{ $t('view_member.block.details.fields.profession') }}</span>
            </vs-col>
            <vs-col vs-w="7">
              <strong v-if="member.$hasEmbedded('profession')">
                {{ member.$getEmbedded('profession').name }}
              </strong>
            </vs-col>
          </vs-row>
          <vs-row class="py-2">
            <vs-col vs-w="5">
              <span>{{ $t('view_member.block.details.fields.case_manager') }}</span>
            </vs-col>
            <vs-col vs-w="7">
              <strong>
                {{ member.$getEmbedded('case-manager').firstname }}&nbsp;{{ member.$getEmbedded('case-manager').lastname }}
              </strong>
            </vs-col>
          </vs-row>
          <vs-row class="py-2">
            <vs-col vs-w="5">
              <span>{{ $t('view_member.block.details.fields.referral_id') }}</span>
            </vs-col>
            <vs-col vs-w="7">
              <strong>{{ member.referralId }}</strong>
            </vs-col>
          </vs-row>
          <vs-row class="py-2" v-if="isCaseManagerTeamOn">
            <vs-col vs-w="5">
              <span>{{ member.hqTeamName }}</span>
            </vs-col>
            <vs-col vs-w="7">
              <strong>{{ member.hqTeamGroupName ? member.hqTeamGroupName : '--' }}</strong>
            </vs-col>
          </vs-row>
        </vx-card>
      </vs-col>

      <vs-col vs-w="3">
        <vx-card class="h-full" :title="$t('view_member.block.activity.title')" v-if="member.stats" :key="refreshKey">
          <vs-row class="py-1">
            <vs-col vs-w="10">
              <span>{{ $t('view_member.block.activity.fields.assessments') }}</span>
            </vs-col>
            <vs-col vs-w="2">
              <strong>{{ member.stats.assessments }}</strong>
            </vs-col>
          </vs-row>
          <vs-row class="py-1">
            <vs-col vs-w="10">
              <span>{{ $t('view_member.block.activity.fields.goals') }}</span>
            </vs-col>
            <vs-col vs-w="2">
              <strong>{{ member.stats.goals }}</strong>
            </vs-col>
          </vs-row>
          <vs-row class="py-1">
            <vs-col vs-w="10">
              <span>{{ $t('view_member.block.activity.fields.moods') }}</span>
            </vs-col>
            <vs-col vs-w="2">
              <strong>{{ member.stats.moods }}</strong>
            </vs-col>
          </vs-row>
          <vs-row class="py-1">
            <vs-col vs-w="10">
              <span>{{ $t('view_member.block.activity.fields.thoughts') }}</span>
            </vs-col>
            <vs-col vs-w="2">
              <strong>{{ member.stats.thoughts }}</strong>
            </vs-col>
          </vs-row>
          <vs-row class="py-1">
            <vs-col vs-w="10">
              <span>{{ $t('view_member.block.activity.fields.videos_watched') }}</span>
            </vs-col>
            <vs-col vs-w="2">
              <strong>{{ member.stats.videosWatched }}</strong>
            </vs-col>
          </vs-row>
          <vs-row class="py-1">
            <vs-col vs-w="10">
              <span>{{ $t('view_member.block.activity.fields.polls_responded') }}</span>
            </vs-col>
            <vs-col vs-w="2">
              <strong>{{ member.stats.poolsResponded }}</strong>
            </vs-col>
          </vs-row>
          <vs-row class="py-1">
            <vs-col vs-w="10">
              <span>{{ $t('view_member.block.activity.fields.comments') }}</span>
            </vs-col>
            <vs-col vs-w="2">
              <strong>{{ member.stats.comments }}</strong>
            </vs-col>
          </vs-row>
          <vs-row class="py-1">
          </vs-row>
        </vx-card>
      </vs-col>
    </vs-row>

    <vs-row class="mt-8">
      <vs-col vs-w="9">
        <vs-row class="gap-8 pr-6">
          <vs-col vs-w="12" class="h-full" v-if="showCheckupInfo">
            <vx-card class="h-full" :title="$t('checkup_information.title')">
              <vs-row class="gap-8">
                <vs-col vs-w="12" v-if="member.$getEmbedded('most-recent-assessment') && member.$getEmbedded('user-assessment-history')">
                  <vs-row>
                    <vs-col vs-w="6">
                      <vx-card class="h-full" :subtitle="$t('assessments.title')" style="text-align:center">
                        <CheckupResultsCharts :values="member.$getEmbedded('most-recent-assessment')"/>
                      </vx-card>
                    </vs-col>
                    <vs-col vs-w="6" class="pl-6">
                      <vx-card class="h-full" :subtitle="$t('products.assessment_history_chart.title')" style="text-align:center"
                              v-if="chartContent && chartContent.length && chartScales && chartScales.length">
                        <AssessmentHistoryWrapper :chartContent="chartContent"
                                                  :scale-definitions="chartScales"></AssessmentHistoryWrapper>
                      </vx-card>
                    </vs-col>
                  </vs-row>
                </vs-col>

                <vs-col vs-w="12" v-if="member.$getEmbedded('user-assessment-symptom-report')">
                  <vs-row>
                    <vs-col vs-w="6" class="h-full">
                      <symptom-report  :memberResource="member" assessmentName="PHQ9"/>
                    </vs-col>
                    <vs-col vs-w="6" class="pl-6 h-full">
                      <symptom-report  :memberResource="member" assessmentName="GAD7"/>
                    </vs-col>
                  </vs-row>
                </vs-col>

                <vs-col vs-w="12" class="h-full" v-if="member.$hasLink('checkup-cadence')">
                  <checkup-cadence :memberResource="member"/>
                </vs-col>
              </vs-row>
            </vx-card>
          </vs-col>

          <vs-col vs-w="12" class="h-full" v-if="member.$hasLink('member-progress-products')">
            <member-progress-viewer ref="progressViewer" :resource="member">
              <template #action="{ productResource, sessionResource }">
                <div class="pt-1 pb-4" v-if="sessionResource.status === 'INITIAL' || sessionResource.status === 'STARTED' || sessionResource.status === 'COMPLETED'">
                  <vs-button v-if="sessionResource.$hasLink('assign-by-email')" type="filled" color="secondary" size="small"
                    @click="onProgressSelected({ product: productResource, session: sessionResource })">
                    Assign by Email
                  </vs-button>
                  <vs-button v-else disabled type="filled" color="secondary" size="small">
                    Assigned
                  </vs-button>
                </div>
              </template>
            </member-progress-viewer>

            <assign-by-email v-if="progressSelected"
                             :opened="assignByEmailStarted"
                             :session-resource="progressSelected.session"
                             @sent="onAssignmentSent"
                             @cancel="onAssignmentCancelled"/>
          </vs-col>

          <vs-col vs-w="12" class="h-full">
            <skill-card  :member-resource="member"/>
          </vs-col>
        </vs-row>
      </vs-col>

      <vs-col vs-w="3" v-if="member.$hasLink('referral-events')">
        <vx-card class="h-full" :title="$t('view_member.block.timeline.title')">
          <member-timeline :member-resource="member"/>
        </vx-card>
      </vs-col>
    </vs-row>
  </div>
</template>

<script>
import predefinedRangesPlugin from '@/components/flatpickr-preset-plugin/plugin';
import VxTimeline from '@/components/timeline/VxTimeline';
import preview from '@/views/app/send_referral/preview';
import AssessmentHistoryWrapper from '@/views/app/view_member/charts/assessment-history-wrapper.vue';
import CheckupResultsBarchart from '@/views/app/view_member/charts/checkup-results-barchart';
import CheckupResultsCharts from '@/views/app/view_member/charts/checkup-results-charts';
import CheckupResultRadarChart from '@/views/app/view_member/charts/checkup-results-radarchart.vue';
import CheckupCadence from '@/views/app/view_member/checkup/checkup-cadence.vue';
import MemberStatsReport from '@/views/app/view_member/member-stats-report';
import MemberTimeline from '@/views/app/view_member/member-timeline';
import AssignByEmail from '@/views/app/view_member/progress/assign/assign-by-email.vue';
import MemberProgressViewer from '@/views/app/view_member/progress/member-progress-viewer';
import SkillCard from '@/views/app/view_member/skills/skills-card.vue';
import SymptomReport from '@/views/app/view_member/symptom_report/symptom-report';
import ReferralViewEmail from '@/views/app/view_referral/referral-view-email';
import 'flatpickr/dist/flatpickr.css';
import { ResourceImpl } from 'hal-client';
import jsPDF from 'jspdf';
import moment from 'moment';
import flatPickr from 'vue-flatpickr-component';

export default {
  name: 'view-member',
  components: {
    CheckupResultsCharts,
    CheckupResultsBarchart,
    CheckupCadence,
    ReferralViewEmail,
    MemberTimeline,
    VxTimeline,
    preview,
    flatPickr,
    jsPDF,
    MemberStatsReport,
    SkillCard,
    CheckupResultRadarChart,
    AssessmentHistoryWrapper,
    SymptomReport,
    MemberProgressViewer,
    AssignByEmail
  },
  props: {
    memberResource: ResourceImpl
  },
  data () {
    return {
      preview: false,
      member: this.memberResource,
      stats: null,
      selectedPeriod: null,
      datepickerConfig: null,
      nowDate: null,
      refreshKey: 0,
      userIdLabel: null,
      hqTeamLabel: null,
      businessUnitLabel: null,
      isCaseManagerTeamOn: false,
      chartContent: [],
      chartScales: [],
      policyNumberLabel: null,
      showPolicyNumberLabel: true,
      assignByEmailStarted: false,
      progressSelected: null
    };
  },
  computed: {
    showCheckupInfo () {
      return (this.member.$getEmbedded('most-recent-assessment') && this.member.$getEmbedded('user-assessment-history')) ||
             this.member.$getEmbedded('user-assessment-symptom-report') ||
             this.member.$hasLink('checkup-cadence');
    }
  },
  mounted () {
    if (this.member.$getEmbedded('user-assessment-history')) {
      this.chartContent = this.member.$getEmbedded('user-assessment-history');
    }
    if (this.member.$getEmbedded('assessment-scales')) {
      this.chartScales = this.member.$getEmbedded('assessment-scales');
    }
    this.member.$request().$get('user-id-label', null, {locale: 'en'}).then((res) => {
      this.userIdLabel = res.state().label;
      if (!this.userIdLabel) {
        this.userIdLabel = this.$t('view_member.block.information.fields.user_id');
      }
    });
    this.member.$request().$get('hq-team-label', null, {locale: 'en'}).then((res) => {
      this.isCaseManagerTeamOn = res.state().enabled;
      this.hqTeamLabel = res.state().label;
      if (!this.hqTeamLabel) {
        this.hqTeamLabel = this.$t('view_member.block.details.fields.team');
      }
    });
    this.member.$request().$get('business-unit-label', null, {locale:'en'}).then((res) => {
      this.businessUnitLabel = res.businessLabel;
    });
    if (this.member.$hasLink('referral-events')) {
      this.member.$request().$get('referral-events').then(res => {
        const newTimeline = res.$getEmbedded('referral-events');
        this.member.referralDate = newTimeline[0].date;
        newTimeline.forEach(element => {
          if (element.type === 'CREATED') this.member.referralDate = element.date;
          if (element.type === 'ACCEPTED') this.member.acceptedDate = element.date;
        });
        this.selectedPeriod = [this.member.acceptedDate, new Date()];
        this.datepickerConfig = {
          mode: 'range',
          dateFormat: 'M j, Y',
          wrap: true,
          defaultDates: [this.member.acceptedDate, new Date()],
          enable: [
            {
              from: moment(this.member.acceptedDate).startOf('day').toDate(),
              to: moment().endOf('day').toDate()
            }
          ],
          plugins: [new predefinedRangesPlugin(this.computePresets(moment(this.member.acceptedDate)))]
        };

        this.onChangeSelectedPeriod(this.selectedPeriod);
      });
    } else {
      this.onChangeSelectedPeriod([this.member.acceptedDate, new Date()]);
    }
    this.member.$request().$get('policy-number-label', null, {locale: 'en'}).then((res) => {
      this.policyNumberLabel = res.state().label;
      this.showPolicyNumberLabel = res.state().enabled;
      if (!this.policyNumberLabel) {
        this.policyNumberLabel = this.$t('view_referral.block.information.claim.fields.policyNumber');
      }
    });
  },
  methods: {
    onViewEmail () {
      this.preview = true;
    },
    onChangeSelectedPeriod (period) {
      this.selectedPeriod = period;
      if (this.member.$hasLink('member-stat')) {
        this.member
          .$request()
          .$get('member-stat', {
            startDate: moment(period[0]).format('YYYY-MM-DD'),
            endDate: moment(period[1]).format('YYYY-MM-DD')
          })
          .then((res) => {
            this.member.stats = res;
            this.refreshKey++;
            this.$forceUpdate();
          });
      }
    },
    computePresets (date) {
      const presets = [];
      presets.push({
        label: 'Since Acceptance',
        start: date.toDate(),
        end: new Date(),
        isDefaultSelected: true
      });

      if (date.isBefore(moment().startOf('week'))) {
        presets.push({
          label: 'This Week',
          start: moment().startOf('week').toDate(),
          end: new Date(),
          isDefaultSelected: false
        });
      }

      if (date.isBefore(moment().subtract(1, 'week').startOf('week'))) {
        presets.push({
          label: 'Last Week',
          start: moment().subtract(1, 'week').startOf('week').toDate(),
          end: moment().startOf('week').subtract(1, 'day').toDate(),
          isDefaultSelected: false
        });
      }

      if (date.isBefore(moment().startOf('month'))) {
        presets.push({
          label: 'This Month',
          start: moment().startOf('month').toDate(),
          end: new Date(),
          isDefaultSelected: false
        });
      }

      if (date.isBefore(moment().subtract(1, 'month').startOf('month'))) {
        presets.push({
          label: 'Last Month',
          start: moment().subtract(1, 'month').startOf('month').toDate(),
          end: moment().startOf('month').subtract(1, 'day').toDate(),
          isDefaultSelected: false
        });
      }

      if (date.isBefore(moment().startOf('quarter'))) {
        presets.push({
          label: 'This Quarter',
          start: moment().startOf('quarter').toDate(),
          end: new Date(),
          isDefaultSelected: false
        });
      }

      if (date.isBefore(moment().subtract(1, 'quarter').startOf('quarter'))) {
        presets.push({
          label: 'Last Quarter',
          start: moment().subtract(1, 'quarter').startOf('quarter').toDate(),
          end: moment().startOf('quarter').subtract(1, 'day').toDate(),
          isDefaultSelected: false
        });
      }

      if (date.isBefore(moment().startOf('year'))) {
        presets.push({
          label: 'This Year',
          start: moment().startOf('year').toDate(),
          end: new Date(),
          isDefaultSelected: false
        });
      }

      if (date.isBefore(moment().subtract(1, 'year').startOf('year'))) {
        presets.push({
          label: 'Last Year',
          start: moment().subtract(1, 'year').startOf('year').toDate(),
          end: moment().startOf('year').subtract(1, 'day').toDate(),
          isDefaultSelected: false
        });
      }


      return presets;
    },
    onProgressSelected (selection) {
      this.progressSelected = selection;
      this.assignByEmailStarted = true;
    },
    onAssignmentSent () {
      this.assignByEmailStarted = false;
      this.$refs.progressViewer.getSessionList();
      this.progressSelected = undefined;
    },
    onAssignmentCancelled () {
      this.assignByEmailStarted = false;
      this.progressSelected = undefined;
    }
  }
};
</script>

<style>
.content-full {
  width: 100%;
  height: 100%;
}

.green-dot {
  height: 1.5em;
  width: 1.5em;
  background-color: #1dcf4c;
  border-radius: 50%;
  display: inline-block;
}

.orange-dot {
  height: 1.5em;
  width: 1.5em;
  background-color: #f28a0a;
  border-radius: 50%;
  display: inline-block;
}

.gray-dot {
  height: 1.5em;
  width: 1.5em;
  background-color: #bbb;
  border-radius: 50%;
  display: inline-block;
}
</style>